/**
 * TV Navigation Context
 * React Context wrapper around TVNavigationEngine for component integration
 */

import React, { createContext, useContext, useRef, useEffect, useState, useCallback } from 'react';
import { TVNavigationEngine } from '../navigation/tvNavigationEngine';
import type { PageNavigationMap, NavigationNode, NavigationDirection } from '../navigation/tvNavigationMap';
import { RefObject } from 'react';

interface TVNavigationContextValue {
  engine: TVNavigationEngine;
  currentPageId: string | null;
  loadPageMap: (pageId: string, map: PageNavigationMap) => void;
  registerNode: (nodeId: string, ref: RefObject<HTMLElement>) => void;
  registerNodeWithData: (node: NavigationNode) => void;
  unregisterNode: (nodeId: string) => void;
  updateNodeNeighbors: (nodeId: string, neighbors: Partial<NavigationNode['neighbors']>) => void;
  focusNode: (nodeId: string, instantScroll?: boolean) => void;
  navigate: (direction: NavigationDirection) => boolean;
  getCurrentNode: () => NavigationNode | null;
  getCurrentNodeId: () => string | null;
  getAllNodes: () => NavigationNode[];
}

const TVNavigationContext = createContext<TVNavigationContextValue | null>(null);

interface TVNavigationProviderProps {
  children: React.ReactNode;
}

/**
 * TV Navigation Provider Component
 * Provides navigation engine to all child components
 */
export function TVNavigationProvider({ children }: TVNavigationProviderProps) {
  const engineRef = useRef<TVNavigationEngine>(new TVNavigationEngine());
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);

  const loadPageMap = useCallback((pageId: string, map: PageNavigationMap) => {
    engineRef.current.loadMap(pageId, map);
    setCurrentPageId(pageId);
  }, []);

  const registerNode = useCallback((nodeId: string, ref: RefObject<HTMLElement>) => {
    engineRef.current.registerNode(nodeId, ref);
  }, []);

  const registerNodeWithData = useCallback((node: NavigationNode) => {
    engineRef.current.registerNodeWithData(node);
  }, []);

  const unregisterNode = useCallback((nodeId: string) => {
    engineRef.current.unregisterNode(nodeId);
  }, []);

  const updateNodeNeighbors = useCallback((
    nodeId: string, 
    neighbors: Partial<NavigationNode['neighbors']>
  ) => {
    engineRef.current.updateNodeNeighbors(nodeId, neighbors);
  }, []);

  const focusNode = useCallback((nodeId: string, instantScroll?: boolean) => {
    engineRef.current.focusNode(nodeId, instantScroll);
  }, []);

  const navigate = useCallback((direction: NavigationDirection): boolean => {
    return engineRef.current.navigate(direction);
  }, []);

  const getCurrentNode = useCallback((): NavigationNode | null => {
    return engineRef.current.getCurrentNode();
  }, []);

  const getCurrentNodeId = useCallback((): string | null => {
    return engineRef.current.getCurrentNodeId();
  }, []);

  const getAllNodes = useCallback((): NavigationNode[] => {
    return engineRef.current.getAllNodes();
  }, []);

  const value: TVNavigationContextValue = {
    engine: engineRef.current,
    currentPageId,
    loadPageMap,
    registerNode,
    registerNodeWithData,
    unregisterNode,
    updateNodeNeighbors,
    focusNode,
    navigate,
    getCurrentNode,
    getCurrentNodeId,
    getAllNodes
  };

  return (
    <TVNavigationContext.Provider value={value}>
      {children}
    </TVNavigationContext.Provider>
  );
}

/**
 * Hook to access TV Navigation context
 */
export function useTVNavigation(): TVNavigationContextValue {
  const context = useContext(TVNavigationContext);
  if (!context) {
    throw new Error('useTVNavigation must be used within TVNavigationProvider');
  }
  return context;
}


